import LocalStorageService from 'web-app/services/local-storage';
import {hasValue} from 'web-app/util/typescript';

export const DebugLocalStorageFlags = {
    FAMLYTICS: {
        flag: 'onlyPrintAnalytics',
        title: 'Toggle Famlytics Debugging on/off',
        description:
            'Toggle this on, to see all Famlytics events printed to the console. Make sure the feature "ENABLE_ANALYTICS" is enabled as well.',
    },
    SHOW_UPCOMING_FEATURES_SETTING: {
        flag: 'SHOW_UPCOMING_FEATURES_SETTING',
        title: 'Toggle "Upcoming feature" settings section on/off',
        description: 'Toggle this on to show the "Upcoming features" tab on the settings page.',
    },
    ENABLE_INTERCOM_TEST_ENVIRONMENT: {
        flag: 'ENABLE_INTERCOM_TEST_ENVIRONMENT',
        title: 'Connect to Intercom test environment',
        description: 'Connect to Intercom test environment for debugging/dev purposes.',
    },
    SHOW_AUTO_LOGOUT_STATS: {
        flag: 'SHOW_AUTO_LOGOUT_STATS',
        title: 'Auto logout V2 status',
        description:
            'Toggle this to get an overlay that shows all details for auto logout. Auto logout feature and V2 needs to be enabled.',
    },
    EDIT_MODAL_PAYMENT_IMPORT: {
        flag: 'EDIT_MODAL_PAYMENT_IMPORT',
        title: 'Enables editing Ready & Incomplete payment rows in the Payments Import',
        description: 'Toggle this on to see the edit modal when clicking on Incomplete Payment Rows.',
    },
    ENABLE_XERO_PAYMENTS: {
        flag: 'ENABLE_XERO_PAYMENTS',
        title: 'Enables Xero payments functionality',
        description: 'Toggle this on to see the Xero payments article link',
    },
    TRACKING_VISUALIZATION: {
        flag: 'TRACKING_VISUALIZATION',
        title: 'Enable tracking visualization',
        description:
            'You can see tracking events for elements, see searchable live feed of events, etc. Make sure the feature "ENABLE_ANALYTICS" is enabled as well.',
    },
    AOC_FOR_EXPORTS: {
        flag: 'AOC_FOR_EXPORTS',
        title: 'Xero exports — new AOC view',
        description: 'Updated version of AOC for Xero exports',
    },
    SHOW_DEFAULT_FTE: {
        flag: 'SHOW_DEFAULT_FTE',
        title: 'Show default fte',
        description: 'Toggle this to show default fte.',
    },
    ENABLE_FINANCE_ONBOARDING: {
        flag: 'ENABLE_FINANCE_ONBOARDING',
        title: 'Enable finance onboarding',
        description: 'Toggle this on to be able to access finance onboarding.',
    },
    ENABLE_FINANCE_ONBOARDING_FREE_US: {
        flag: 'ENABLE_FINANCE_ONBOARDING_FREE_US',
        title: 'Enable free US finance onboarding',
        description: 'Toggle this on to be able to access free US finance onboarding.',
    },
    ENABLE_FINANCE_ONBOARDING_PAID_US: {
        flag: 'ENABLE_FINANCE_ONBOARDING_PAID_US',
        title: 'Enable paid US finance onboarding',
        description: 'Toggle this on to be able to access paid US finance onboarding.',
    },
    ENABLE_BOOKING_PATTERN_BUILDER: {
        flag: 'ENABLE_BOOKING_PATTERN_BUILDER',
        title: 'Enable booking pattern builder',
        description: 'Toggle this on to access the new booking pattern builder',
    },
    ENABLE_FORM_BUILDER_DEBUG: {
        flag: 'ENABLE_FORM_BUILDER_DEBUG',
        title: 'Enable form builder debug info',
        description: 'Toggle this on to access the form builder debug info',
    },
    ENABLE_BULK_PATTERN_FLOW: {
        flag: 'ENABLE_BULK_PATTERN_FLOW',
        title: 'Enable bulk pattern creation flow',
        description: 'Toggle this on to access the bulk pattern creation flow',
    },
    ENABLE_CUSTOM_INQUIRIES: {
        flag: 'ENABLE_CUSTOM_INQUIRIES',
        title: 'Enable custom inquiries',
        description: 'Toggle this to be able to use custom inquiries',
    },
    FORM_BUILDER_DEBUG_INFO: {
        flag: 'FORM_BUILDER_DEBUG_INFO',
        title: 'Adds debug info to the form builder state (for e2e)',
        description: 'Toggle this to add debug info to the form builder state (for e2e)',
    },
    SHOW_FINANCE_DEBUGGING_BUTTON: {
        flag: 'SHOW_FINANCE_DEBUGGING_BUTTON',
        title: 'Show finance debugging button',
        description: 'Show finance debugging button to access Finance debugging page.',
    },
    SHOW_BULK_PLAN_DELETE: {
        flag: 'SHOW_BULK_PLAN_DELETE',
        title: 'Show Bulk Plan delete option',
        description: 'Toggle this to show the bulk delete latest plan in plans page',
    },
    TRACE_ALL_TRANSACTIONS: {
        flag: 'TRACE_ALL_TRANSACTIONS',
        title: '[Sentry] Trace all transactions',
        description: 'Turn this on to have all transactions traced in Sentry.',
    },
    ENABLE_MANAGE_CHARGES: {
        flag: 'ENABLE_MANAGE_CHARGES',
        title: 'Enables ability to manage charges (US Free)',
        description:
            'Toggle this on to see the simplified version of managing plans for children called "Manage Charges". This is part of the simplified finance setup for US.',
    },
    ENABLE_ORG_REQUEST_SITE: {
        flag: 'ENABLE_ORG_REQUEST_SITE',
        title: 'Enable organization request new site',
        description: 'Toggle this on to access the button that allows organizations to request a site',
    },
    ENABLE_SITE_OVERVIEW_V2: {
        flag: 'ENABLE_SITE_OVERVIEW_V2',
        title: 'Enable new Site Overview in Famly Home',
    },
} satisfies {
    [flagId: string]: {
        flag: string;
        title: string;
        description?: string;
    };
};

/**
 * I grew tired of having to import DebugLocalStorageFlags to check if a flag is enabled
 * so I added an overload that allows you to simply look up by the union of keys in DebugLocalStorageFlags
 */
interface CheckIfFlagIsToggled {
    (flag: string): boolean;
    (flag: keyof typeof DebugLocalStorageFlags): boolean;
}
export const checkIfFlagIsToggled: CheckIfFlagIsToggled = flag => {
    const lookup = DebugLocalStorageFlags[flag];
    if (hasValue(lookup)) {
        return Boolean(LocalStorageService.getFromLocalStorage(lookup.flag));
    }
    return Boolean(LocalStorageService.getFromLocalStorage(flag));
};
